'use strict';

const updateHtml = (html, token, value) => window._theme.Helpers.updateHtml(html, token, value);

const formatMoney = (cents) => {
  if (typeof window.Shopify?.formatMoney === 'function') {
    return window.Shopify.formatMoney(Number(cents) || 0, window.money_format);
  }

  return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
};

const getProductTags = (item) => {
  if (Array.isArray(item.tags)) {
    return item.tags.map((tag) => String(tag).toLowerCase());
  }

  return String(item.tags || '')
    .toLowerCase()
    .split(',')
    .map((tag) => tag.trim());
};

const getFirstAvailableVariant = (product) => {
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  return variants.find((variant) => variant.available) || variants[0] || null;
};

const WISHLIST_SHARE_COPY = {
  copy: 'Copy link',
  copied: 'Copied',
  success: 'Link copied to clipboard',
  error: 'Unable to copy link. Select the URL and copy it manually.'
};

class AccountWishlist extends HTMLElement {

  abortController = null;
  modalTrigger = null;
  copyStatusTimer = null;
  pendingRemoveProductId = null;
  pendingRemoveProductTitle = '';
  selectors = {};

  connectedCallback() {
    this.selectors = {
      itemsContainer: this.querySelector('[data-wishlist-items]'),
      loadingMessage: this.querySelector('[data-wishlist-loading]'),
      emptyMessage: this.querySelector('[data-wishlist-empty]'),
      errorMessage: this.querySelector('[data-wishlist-error]'),
      announceMessage: this.querySelector('[data-wishlist-announce]'),
      shareButton: this.querySelector('[data-wishlist-share-btn]'),
      shareModal: this.querySelector('[data-wishlist-share-modal]')
    };

    if (this.selectors.shareModal) {
      this.selectors.shareUrlInput = this.selectors.shareModal.querySelector('[data-wishlist-share-url-input]');
      this.selectors.shareCopyBtn = this.selectors.shareModal.querySelector('[data-wishlist-share-copy]');
      this.selectors.shareCopyStatus = this.selectors.shareModal.querySelector('#WishlistShareCopyStatus');
      this.selectors.shareCopyNotice = this.selectors.shareModal.querySelector('[data-wishlist-share-notice]');
      this.selectors.shareModal.addEventListener('close', this.onShareModalClose);
    }

    if (!this.selectors.itemsContainer) return;

    this.addEventListener('click', this.onPageClick, true);
    this.addEventListener('keydown', this.onShareKeydown);
    document.addEventListener('wishlist:changed', this.onWishlistChanged);
    this.loadWishlistPage();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.copyStatusTimer) clearTimeout(this.copyStatusTimer);
    this.selectors.shareModal?.removeEventListener('close', this.onShareModalClose);
    this.removeEventListener('click', this.onPageClick, true);
    this.removeEventListener('keydown', this.onShareKeydown);
    document.removeEventListener('wishlist:changed', this.onWishlistChanged);
  }

  onWishlistChanged = (event) => {
    // Heart remove on this page: drop that card only (do not rebuild grid).
    if (event.detail?.action === 'removed' || this.pendingRemoveProductId) {
      this.removeWishlistCard();
      return;
    }

    this.loadWishlistPage();
  };

  async loadWishlistPage() {
    if (!this.selectors.itemsContainer) return;

    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.showLoadingState(true);

    try {
      const wishlistData = await this.fetchWishlistItems(controller.signal);
      const items = await this.fetchProductsJson(wishlistData.items, controller.signal);
      if (controller.signal.aborted) return;

      this.renderProductCards({ ...wishlistData, items });
    } catch (error) {
      if (error.name === 'AbortError') return;

      console.error('Unable to load Smart Wishlist products:', error);
      this.selectors.shareButton?.toggleAttribute('hidden', true);
      this.updatePageStatus('error');
    } finally {
      if (this.abortController === controller) this.showLoadingState(false);
    }
  }

  async removeWishlistCard() {
    if (!this.selectors.itemsContainer) return;

    const productTitle = this.pendingRemoveProductTitle;
    this.setGridUpdating(true);
    this.announceStatus(this.selectors.itemsContainer.dataset.removingText || 'Removing from wishlist...');

    // Keep loader visible briefly so users can see the update.
    await new Promise(function(resolve) {
      setTimeout(resolve, 350);
    });

    try {
      this.pendingRemoveProductId =
        this.pendingRemoveProductId ||
        document.querySelector('.sidecart-qv.active .smartwishlist')?.getAttribute('data-product');

      if (this.pendingRemoveProductId) {
        this.removeCardByProductId(this.pendingRemoveProductId);
      } else {
        for (const card of this.selectors.itemsContainer.querySelectorAll('.grid-item.is-removing')) {
          card.remove();
        }
      }

      const remaining = this.selectors.itemsContainer.querySelectorAll('.grid-item').length;
      this.syncWishlistBadgeCount(remaining);
      this.selectors.shareButton?.toggleAttribute('hidden', remaining === 0);

      this.announceRemoved(productTitle);

      if (remaining === 0) {
        this.selectors.itemsContainer.innerHTML = '';
        this.updatePageStatus('empty');
      }
    } finally {
      this.setGridUpdating(false);
      this.pendingRemoveProductId = null;
      this.pendingRemoveProductTitle = '';
    }
  }

  removeCardByProductId(productId) {
    if (!productId || !this.selectors.itemsContainer) return;

    const id = String(productId);
    const card =
      this.selectors.itemsContainer.querySelector(`.product-item[data-id="${id}"]`) ||
      this.selectors.itemsContainer.querySelector(`.smartwishlist[data-product="${id}"]`)?.closest(
        '.product-item'
      );

    card?.closest('.grid-item')?.remove();
  }

  setGridUpdating(isUpdating) {
    this.selectors.itemsContainer?.classList.toggle('is-updating', isUpdating);
    this.selectors.itemsContainer?.setAttribute('aria-busy', String(isUpdating));
  }

  announceRemoved(productTitle) {
    const container = this.selectors.itemsContainer;
    if (!container) return;

    const namedTemplate = container.dataset.removedNamedText || '';
    const fallback = container.dataset.removedText || 'Removed from wishlist';
    this.announceStatus(
      productTitle ? namedTemplate.replace('__TITLE__', productTitle) : fallback
    );
  }

  announceStatus(message) {
    const announce = this.selectors.announceMessage;
    if (!announce || !message) return;

    // Clear first so the same/similar message is announced again.
    announce.textContent = '';
    window.requestAnimationFrame(() => {
      announce.textContent = message;
    });
  }

  syncWishlistBadgeCount(count) {
    const safeCount = Math.max(0, parseInt(count, 10) || 0);
    try {
      window.localStorage.setItem('smart_wishlist_count', String(safeCount));
    } catch (err) {}
    document.querySelectorAll('.topbadge, [data-wishlist-count]').forEach((el) => {
      el.textContent = safeCount ? String(safeCount) : '';
      el.style.display = safeCount ? '' : 'none';
    });
  }

  async fetchWishlistItems(signal) {
    const customerId = window._theme?.settings?.customer?.id;
    const wishlistId = typeof window.SWGetExpressWishlistId === 'function' ? window.SWGetExpressWishlistId() : '';
    const url = new URL('/a/wishlist', window.location.origin);

    url.searchParams.set('type', 'api');
    url.searchParams.set('version', '1');
    url.searchParams.set('_', String(Date.now()));

    if (customerId) {
      url.searchParams.set('customerid', String(customerId));
    } else if (wishlistId) {
      url.searchParams.set('wishlistid', String(wishlistId));
    } else {
      throw new Error('Smart Wishlist customer information is unavailable');
    }

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      cache: 'no-store',
      signal
    });

    if (!response.ok) {
      throw new Error(`Wishlist request failed with status ${response.status}`);
    }

    const data = await response.json();
    if (data.type !== 'success' || !Array.isArray(data.items)) {
      throw new Error('Smart Wishlist returned an invalid response');
    }

    return {
      count: Number(data.count ?? data.items.length),
      items: data.items
    };
  }

  async fetchProductsJson(items, signal) {
    if (items.length === 0) return items;

    const requestLimit = 20;
    const requests = [];
    const productsById = new Map();
    const productsByHandle = new Map();

    for (let index = 0; index < items.length; index += requestLimit) {
      const handles = items
        .slice(index, index + requestLimit)
        .map((item) => item.handle)
        .filter(Boolean);

      if (handles.length === 0) continue;

      // Commas must stay unencoded so Liquid can split the handles.
      const url = `/?view=__DoNoTUse__&handles=${handles.map(encodeURIComponent).join(',')}`;
      requests.push(
        fetch(url, { signal })
          .then((response) => (response.ok ? response.json() : { products: [] }))
          .catch(() => ({ products: [] }))
      );
    }

    const results = await Promise.allSettled(requests);
    if (signal.aborted) throw new DOMException('Wishlist request aborted', 'AbortError');

    for (const result of results) {
      if (result.status !== 'fulfilled') continue;

      for (const product of result.value.products ?? []) {
        if (!product?.id) continue;
        productsById.set(Number(product.id), product);
        if (product.handle) productsByHandle.set(String(product.handle), product);
      }
    }

    return items.map((item) => {
      const product = productsById.get(Number(item.id)) || productsByHandle.get(String(item.handle));

      if (!product) return item;

      const selectedVariant =
        (product.variants || []).find(
          (variant) => Number(variant.id) === Number(item.variant_id)
        ) || getFirstAvailableVariant(product);

      return {
        ...item,
        ...product,
        variant_id: selectedVariant?.id || item.variant_id,
        available: product.available,
        review_summary_metadata:
          product.review_summary_metadata || item.review_summary_metadata
      };
    });
  }

  renderProductCards(data) {
    const sortedItems = [...data.items].sort(
      (first, second) => Number(Boolean(second.available)) - Number(Boolean(first.available))
    );

    this.syncWishlistBadgeCount(data.count);

    this.selectors.shareButton?.toggleAttribute('hidden', sortedItems.length === 0);

    if (sortedItems.length === 0) {
      this.selectors.itemsContainer.innerHTML = '';
      this.updatePageStatus('empty');
      return;
    }

    const template = window._theme?.product_html?.productGridNew;
    if (!template) throw new Error('productGridNew template is unavailable');

    this.updatePageStatus('items');
    this.selectors.itemsContainer.innerHTML = sortedItems
      .map((item) => this.buildProductCard(template, item))
      .join('');
    this.selectors.itemsContainer.setAttribute('aria-busy', 'false');

    window.initializeElements?.();
    this.initSmartWishlistHearts();
  }

  updatePageStatus(status) {
    this.selectors.loadingMessage?.toggleAttribute('hidden', status !== 'loading');
    this.selectors.emptyMessage?.toggleAttribute('hidden', status !== 'empty');
    this.selectors.errorMessage?.toggleAttribute('hidden', status !== 'error');
    this.selectors.itemsContainer?.toggleAttribute('hidden', status !== 'items');
    this.selectors.itemsContainer?.setAttribute('aria-busy', String(status === 'loading'));
  }

  showLoadingState(loading) {
    if (loading) {
      this.selectors.itemsContainer.innerHTML = '';
      this.updatePageStatus('loading');
    }
    window._theme?.toggleLoading?.(loading ? 1 : undefined);
  }

  async initSmartWishlistHearts() {
    for (let attempt = 0; attempt < 60; attempt += 1) {
      if (!this.isConnected) return;
      if (typeof window.SmartWishlistMain === 'function') {
        window._theme?.initSmartWishlist?.();
        const wishlist = this;
        setTimeout(function() {
          wishlist.setHeartAriaLabels();
        }, 300);
        return;
      }
      await new Promise(function(resolve) {
        setTimeout(resolve, 250);
      });
    }
  }

  setHeartAriaLabels() {
    const container = this.selectors.itemsContainer;
    if (!container) return;

    for (const heart of container.querySelectorAll('.smartwishlist')) {
      const title = heart.getAttribute('data-title') || '';
      const label = title ? `Remove ${title} from wishlist` : 'Remove from wishlist';
      const control = heart.querySelector('a, button');

      if (control) {
        if (!control.getAttribute('aria-label')) control.setAttribute('aria-label', label);
        continue;
      }

      if (!heart.getAttribute('aria-label')) heart.setAttribute('aria-label', label);
      heart.setAttribute('role', 'button');
      heart.setAttribute('tabindex', '0');
    }
  }

  /* Share modal + heart click */
  onPageClick = (event) => {
    const heart = event.target.closest('.smartwishlist');
    if (heart?.getAttribute('data-product') && heart.classList.contains('bookmarked')) {
      this.pendingRemoveProductId = heart.getAttribute('data-product');
      this.pendingRemoveProductTitle = heart.getAttribute('data-title') || '';
      heart.closest('.grid-item')?.classList.add('is-removing');
    }

    if (event.target === this.selectors.shareModal) {
      this.closeShareModal();
      return;
    }

    if (event.target.closest('[data-wishlist-share-close], #footerbtn')) {
      this.closeShareModal();
      return;
    }

    if (event.target.closest('[data-wishlist-share-copy]')) {
      event.preventDefault();
      this.copyShareUrl();
      return;
    }

    const shareButton = event.target.closest('[data-wishlist-share-btn]');
    if (!shareButton) return;

    this.openShareModal(shareButton, this.getWishlistShareUrl());
  };

  onShareKeydown = (event) => {
    const heart = event.target.closest('.smartwishlist[role="button"]');
    if (heart && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      heart.click();
    }
  };

  onShareModalClose = () => {
    this.clearShareCopyFeedback();
    this.modalTrigger?.focus({ preventScroll: true });
  };

  getWishlistShareUrl() {
    if (typeof window.SWGetCustomerWishlistId !== 'function') return '';

    const wishlistId = window.SWGetCustomerWishlistId();
    return wishlistId ? `${window.location.origin}/a/wishlist/${encodeURIComponent(wishlistId)}` : '';
  }

  openShareModal(trigger, shareUrl) {
    if (!this.selectors.shareModal || !shareUrl) return;

    this.modalTrigger = trigger;
    this.setShareSocialLinks(shareUrl);
    this.clearShareCopyFeedback();
    this.selectors.shareModal.showModal();

    const wishlist = this;
    const modal = this.selectors.shareModal;

    setTimeout(function() {
      if (!modal.open || !window.jQuery || typeof window._theme?.focusTrap !== 'function') return;

      window._theme.focusTrap(window.jQuery(modal), function() {
        wishlist.closeShareModal();
      });
    }, 0);
  }

  closeShareModal() {
    if (!this.selectors.shareModal?.open) return;
    this.selectors.shareModal.close();
  }

  setShareSocialLinks(shareUrl) {
    const encodedUrl = encodeURIComponent(shareUrl);
    const customerEmail = window._theme?.settings?.customer?.email ?? '';

    const links = {
      '.social-logo-fb': `https://www.facebook.com/sharer.php?u=${encodedUrl}`,
      '.social-logo-twitter': `https://twitter.com/intent/tweet?url=${encodedUrl}`,
      '.social-logo-linkedin': `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}`,
      '.social-logo-pinterest': `https://pinterest.com/pin/create/button/?url=${encodedUrl}`,
      '.social-logo-messanger': `https://msng.link/o/?${encodedUrl}=fm`,
      '.social-logo-whatsapp': `https://api.whatsapp.com/send?text=${encodeURIComponent(`My Wishlist ${shareUrl}`)}`,
      '.social-logo-mail': `mailto:${encodeURIComponent(customerEmail)}?subject=My%20Wishlist&body=${encodedUrl}`
    };

    for (const [selector, href] of Object.entries(links)) {
      this.selectors.shareModal.querySelector(selector)?.setAttribute('href', href);
    }

    if (this.selectors.shareUrlInput) {
      this.selectors.shareUrlInput.value = shareUrl;
    }
  }

  announceShareCopyMessage(message) {
    const { shareCopyStatus, shareCopyBtn } = this.selectors;
    if (!shareCopyStatus) return;

    shareCopyStatus.textContent = '';

    setTimeout(function() {
      shareCopyStatus.textContent = message;
      shareCopyStatus.setAttribute('tabindex', '-1');
      shareCopyStatus.focus({ preventScroll: true });

      setTimeout(function() {
        shareCopyStatus.removeAttribute('tabindex');
        shareCopyBtn?.focus({ preventScroll: true });
      }, 300);
    }, 50);
  }

  clearShareCopyFeedback() {
    if (this.copyStatusTimer) {
      clearTimeout(this.copyStatusTimer);
      this.copyStatusTimer = null;
    }

    const { shareCopyBtn, shareCopyStatus, shareCopyNotice } = this.selectors;

    if (shareCopyBtn) {
      shareCopyBtn.textContent = WISHLIST_SHARE_COPY.copy;
    }

    if (shareCopyNotice) {
      shareCopyNotice.textContent = '';
      shareCopyNotice.hidden = true;
    }

    if (shareCopyStatus) {
      shareCopyStatus.textContent = '';
    }
  }

  async copyShareUrl() {
    const { shareUrlInput, shareCopyBtn } = this.selectors;
    const shareUrl = shareUrlInput?.value?.trim();

    if (!shareUrl || !shareCopyBtn) return;

    let copied = false;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        copied = true;
      }
    } catch (error) {
      copied = false;
    }

    if (!copied && shareUrlInput) {
      shareUrlInput.select();
      copied = document.execCommand('copy');
      shareCopyBtn.focus({ preventScroll: true });
    }

    if (!copied) {
      this.announceShareCopyMessage(WISHLIST_SHARE_COPY.error);
      return;
    }

    shareCopyBtn.textContent = WISHLIST_SHARE_COPY.copied;

    if (this.selectors.shareCopyNotice) {
      this.selectors.shareCopyNotice.textContent = WISHLIST_SHARE_COPY.success;
      this.selectors.shareCopyNotice.hidden = false;
    }

    this.announceShareCopyMessage(WISHLIST_SHARE_COPY.success);
    const wishlist = this;
    this.copyStatusTimer = setTimeout(function() {
      wishlist.clearShareCopyFeedback();
    }, 3000);
  }

  /* Product cards */
  buildProductCard(template, item) {
    const variants = Array.isArray(item.variants) ? item.variants : [];
    const selectedVariant = variants.find((variant) => Number(variant.id) === Number(item.variant_id)) || getFirstAvailableVariant(item);
    const productImages = this.buildProductImages(item);
    
    const shippingVariants = variants
      .filter((variant) => variant.available)
      .map((variant) => ({
        barcode: variant.barcode || '',
        available: variant.available,
        brightperlInventory: variant.brightperl_inventory || '',
        allowExpressShipping: variant.allow_express_shipping
      }));

    let productHtml = template;
    productHtml = updateHtml(productHtml, '[[Product_Grid_Class]]', item.available === false ? 'sold_out' : '');
    productHtml = updateHtml(productHtml, '[[Product_Variant_Class]]', variants.length === 1 ? 'single-variant' : 'multiple-variant');
    productHtml = updateHtml(productHtml, '[[Product_Id]]', item.id);
    productHtml = updateHtml(productHtml, '[[Variant_Id]]', selectedVariant?.id || item.variant_id);
    productHtml = updateHtml(productHtml, '[[Product_Json]]', this.buildProductJsonScript(item, selectedVariant));
    productHtml = updateHtml(productHtml, '[[Product_GWP_Properties]]', '');
    productHtml = updateHtml(productHtml, '[[Image_1_Class]]', productImages.imageClass);
    productHtml = updateHtml(productHtml, '[[Product_Image_1]]', productImages.image1);
    productHtml = updateHtml(productHtml, '[[Product_Image_2]]', productImages.image2);
    productHtml = updateHtml(productHtml, '[[Product_Labels]]', this.buildProductLabels(item));
    productHtml = updateHtml(productHtml, '[[Product_Title]]', item.title);
    productHtml = updateHtml(productHtml, '[[Product_Vendor]]', item.vendor || '');
    productHtml = updateHtml(productHtml, '[[Product_Type_Meta]]', item.product_type || '');
    productHtml = updateHtml(productHtml, '[[Product_URL]]', this.getProductLink(item));
    productHtml = updateHtml(productHtml, '[[Product_Price]]', this.buildProductPrice(item));
    productHtml = updateHtml(productHtml, '[[Trustpilot_Review_Summary]]', this.buildTrustpilotReviewSummary(item));
    productHtml = updateHtml(productHtml, '[[shipping_info_variants]]', JSON.stringify(shippingVariants).replace(/'/g, '&#39;'));
    productHtml = updateHtml(productHtml, '[[Product_QV_ATC_Btn]]', this.buildQuickViewButtons(item, selectedVariant));
    productHtml = updateHtml(productHtml, '[[Product_Notify_Me_Btn]]', '');

    return productHtml;
  }

  buildProductJsonScript(item, selectedVariant) {
    const vid = selectedVariant?.id || item.variant_id || item.id;
    const price = Number(selectedVariant?.price ?? item.price_min ?? 0);
    const comparePrice = Number(selectedVariant?.compare_at_price ?? item.compare_at_price_min ?? 0);

    return `<script type="application/json" data-product-json>${JSON.stringify({
      id: item.id,
      title: item.title || '',
      vendor: item.vendor || '',
      type: item.product_type || '',
      handle: item.handle || '',
      available: Boolean(item.available),
      variant_id: vid,
      selected_or_first_available_variant_id: vid,
      price: Math.round(price * 100),
      compare_at_price: Math.round(comparePrice * 100),
    })}</script>`;
  }

  buildProductImages(item) {
    const primary = item.featured_image || item.image || '';
    const secondary = item.second_image || '';

    const image1 = primary ? `<img src="${primary}" alt="${item.title || ''}" loading="lazy" width="600" height="600" class="lazyload">` : '';
    
    const image2 = secondary ? `<div class="images-two"><img src="${secondary}" alt="" loading="lazy" width="600" height="600" class="lazyload"></div>` : '';

    return {
      image1,
      image2,
      imageClass: secondary ? 'images-one' : ''
    };
  }

  buildProductLabels(item) {
    let html = '';
    const maxDiscount = this.getMaxSaveAmount(item);
    const tags = getProductTags(item);

    if (maxDiscount > 0) {
      html += `<span class="new-tag-style blue-badge-style">Save Up To $${maxDiscount}</span>`;
    }
    if (item.available === false) {
      html += '<div class="new-tag-style soldout">Out Of Stock</div>';
    }
    if (tags.includes('online-only-item')) {
      html += '<div class="new-tag-style">Online Only</div>';
    }
    if (tags.includes('new')) {
      html += '<div class="new-tag-style">New!</div>';
    }

    return html ? `<div class="product-tags-wrap">${html}</div>` : '';
  }

  buildProductPrice(item) {
    if (item.available === false) return '';

    const variants = Array.isArray(item.variants) ? item.variants : [];
    const prices = variants.map((variant) => Number(variant.price) || 0).filter((price) => price > 0);
    const priceMin = Number(item.price_min) || (prices.length ? Math.min(...prices) : 0);
    const priceMax = Number(item.price_max) || (prices.length ? Math.max(...prices) : 0);

    if (prices.length > 1 && priceMin !== priceMax && priceMin > 0) {
      const minPrice = formatMoney(priceMin);
      const maxPrice = formatMoney(priceMax);
      return `<div class="priceContainer"><div class="priceRange"><span class="visually-hidden">Price range from ${minPrice} to ${maxPrice}</span><span aria-hidden="true">${minPrice} - ${maxPrice}</span></div></div>`;
    }

    const variant = getFirstAvailableVariant(item);
    if (variant) {
      const price = formatMoney(variant.price);
      const compareAt = Number(variant.compare_at_price) || 0;

      if (compareAt > Number(variant.price)) {
        const comparePrice = formatMoney(compareAt);
        return `<div class="priceContainer"><div class="priceRange"><div class="price-list"><span class="visually-hidden visually-hidden--inline">Sale Price</span>${price}<span class="visually-hidden">Regular Price</span><span class="del-price price-item--regular" aria-hidden="false">${comparePrice}</span></div></div></div>`;
      }

      return `<div class="priceContainer"><div class="priceRange"><span class="visually-hidden visually-hidden--inline">Sale Price</span>${price}</div></div>`;
    }

    const wishlistPrice = item.variant_price ?? item.price;
    if (wishlistPrice != null && wishlistPrice !== '') {
      return `<div class="priceContainer"><div class="priceRange"><span class="visually-hidden visually-hidden--inline">Sale Price</span>${formatMoney(Math.round(Number(wishlistPrice) * 100))}</div></div>`;
    }

    return '';
  }

  buildTrustpilotReviewSummary(item) {
    const metadata = item.review_summary_metadata;
    if (!metadata) return '';

    return `<div class="trustpilot-review-summary-wrapper">
              <trustpilot-review-summary
                data-show-popup="false"
                data-show-total="true"
                data-show-average="true"
                data-review-summary-metadata="${String(metadata).replace(/"/g, '&quot;')}"
                data-product-url="${this.getProductLink(item)}"
                data-product-id="${item.id}">
              </trustpilot-review-summary>
            </div>`;
  }

  buildQuickViewButtons(item, selectedVariant) {
    const variantId = selectedVariant?.id || item.variant_id;
    const productUrl = this.getProductPathname(item);

    if (!variantId || !productUrl) return '';

    const isAvailable = item.available !== false;
    const isSingleVariant = Array.isArray(item.variants) && item.variants.length === 1;

    if (isSingleVariant && isAvailable) {
      return `<div class="offer-wrapper fix-bottom">
              <div class="d-flex justify-content-between grid-btns">
                <button type="button" class="offer-quickview-mob-button cm-add-bndle primary-button-color" aria-label="Add To Cart ${item.title || ''}"
                  data-vid="${variantId}" data-id="qv-${item.id}" data-url="${productUrl}">
                  <span>Add To Cart</span>
                </button>
              </div>
            </div>`;
    }

    const label = 'Quick View';

    return `<div class="offer-wrapper fix-bottom">
              <div class="d-flex justify-content-between grid-btns">
                <button type="button" class="offer-quickview-mob-button hide-desk" aria-label="${label} ${item.title || ''}" aria-haspopup="dialog" aria-controls="model--quickview"
                  data-vid="${variantId}" data-id="qv-${item.id}" data-url="${productUrl}" data-mobile-quickview-button>
                  <span>${label}</span>
                </button>
                <button type="button" class="offer-quickview-mob-button hide-mob" aria-label="${label} ${item.title || ''}" aria-haspopup="dialog" aria-controls="model--quickview"
                  data-vid="${variantId}" data-id="qv-${item.id}" data-url="${productUrl}" data-default-quickview-button>
                  <span>${label}</span>
                </button>
              </div>
            </div>`;
  }

  getMaxSaveAmount(product) {
    let maxDiscount = 0;

    for (const variant of product.variants || []) {
      const compareAt = Number(variant.compare_at_price) || 0;
      const price = Number(variant.price) || 0;

      if (compareAt <= price) continue;

      const discount = Math.ceil((compareAt - price) / 100);
      if (discount > maxDiscount) maxDiscount = discount;
    }

    return maxDiscount;
  }

  getProductLink(item) {
    if (item.url) {
      try {
        const productUrl = new URL(String(item.url), window.location.origin);
        if (item.variant_id) productUrl.searchParams.set('variant', String(item.variant_id));
        return `${productUrl.pathname}${productUrl.search}`;
      } catch (error) {
        console.error('Error getting product URL:', error);
      }
    }

    if (!item.handle) return '';

    return item.variant_id ? `/products/${item.handle}?variant=${item.variant_id}` : `/products/${item.handle}`;
  }

  getProductPathname(item) {
    if (item.url) {
      try {
        return new URL(String(item.url), window.location.origin).pathname;
      } catch (error) {
        console.error('Error getting product path:', error);
      }
    }

    return item.handle ? `/products/${item.handle}` : '';
  }
}

if (!customElements.get('account-wishlist')) {
  customElements.define('account-wishlist', AccountWishlist);
}